/**
 * useRotationHealth(userId) — shared per-peer rotation-health lookup.
 *
 * Returns `'healthy' | 'slipping' | 'inactive' | null` for the given
 * user id, deriving the state from the server's key-history rows
 * under the same thresholds that RotationCadenceSparkline uses.
 *
 * Design notes:
 *   - A module-level cache dedupes concurrent fetches so rendering
 *     a 50-row conversation list only ever hits `/key-history` once
 *     per distinct peer.
 *   - Cache entries carry a `fetchedAt` stamp and expire after
 *     CACHE_TTL_MS (15 min) — the health state changes at most
 *     once per 7-day rotation cycle, so a generous TTL is safe.
 *   - Duress-safe: returns `null` without firing any request when
 *     `sessionStorage['gs_duress_mode']` is set; the decoy-mode UI
 *     must never probe the real `/key-history` endpoint.
 *   - `null` also signals "not enough data yet" (loading, empty,
 *     or network error) so callers can render a neutral state.
 *
 * Export `classifyRotationHealth(entries)` too so non-React call
 * sites (e.g. RotationCadenceSparkline) can share the same logic.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CACHE_TTL_MS = 15 * 60 * 1000;

const HEALTH_GREEN_MAX_DAYS = 10;
const HEALTH_AMBER_MAX_DAYS = 21;

/**
 * Classify health from a list of `{ts}` entries sorted ascending.
 * Uses the same worst-gap semantics as RotationCadenceSparkline:
 * includes the gap from the most recent rotation to now.
 */
export const classifyRotationHealth = (entries) => {
  if (!entries || entries.length === 0) return null;
  let worstMs = Date.now() - entries[entries.length - 1].ts;
  for (let i = 1; i < entries.length; i++) {
    const gap = entries[i].ts - entries[i - 1].ts;
    if (gap > worstMs) worstMs = gap;
  }
  const days = Math.round(worstMs / 86400_000);
  if (days <= HEALTH_GREEN_MAX_DAYS) return 'healthy';
  if (days <= HEALTH_AMBER_MAX_DAYS) return 'slipping';
  return 'inactive';
};

/**
 * Derive a "cadence personality" from the same timestamp list the
 * classifier uses. Returns `{ inferredCadenceDays, streak }`.
 *
 *   inferredCadenceDays — median of the gaps between consecutive
 *     rotations (integer days, ≥1). Median is robust to a single
 *     long gap that would skew the mean and fool the badge into
 *     saying "14d cadence" when the user actually rotates every
 *     3 days except for one holiday.
 *
 *   streak — length of the longest TAIL-anchored run of
 *     consecutive rotations where each gap is within tolerance
 *     (±50% or ±1 day, whichever is larger) of the inferred
 *     cadence. Tail-anchored means we count back from the most
 *     recent rotation — a streak that broke two months ago
 *     doesn't count. A solo rotation is a 1-length streak.
 *
 * Returns null when there's nothing useful to say (no rotations
 * at all, or only one rotation so we can't infer a cadence).
 */
export const computeCadenceSignature = (entries) => {
  if (!entries || entries.length < 2) {
    // A single rotation is still a streak of 1 — surface it so
    // brand-new accounts don't look cadence-less.
    if (entries && entries.length === 1) {
      return { inferredCadenceDays: null, streak: 1 };
    }
    return null;
  }

  // Gaps in days, sorted for median.
  const gapsDays = [];
  for (let i = 1; i < entries.length; i++) {
    const d = (entries[i].ts - entries[i - 1].ts) / 86400_000;
    if (d > 0) gapsDays.push(d);
  }
  if (gapsDays.length === 0) return { inferredCadenceDays: null, streak: 1 };

  const sorted = [...gapsDays].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const medianDays =
    sorted.length % 2 === 0
      ? (sorted[mid - 1] + sorted[mid]) / 2
      : sorted[mid];
  const inferredCadenceDays = Math.max(1, Math.round(medianDays));

  // Tolerance: 50% of the inferred cadence, but always ≥1 day so
  // a 2d cadence doesn't demand millisecond precision.
  const tolDays = Math.max(1, inferredCadenceDays * 0.5);

  // Walk backwards from the most recent rotation; count how many
  // consecutive "good" gaps we see before a gap blows the
  // tolerance. Include the anchor rotation itself (+1).
  let streak = 1;
  for (let i = gapsDays.length - 1; i >= 0; i--) {
    if (Math.abs(gapsDays[i] - inferredCadenceDays) <= tolDays) {
      streak += 1;
    } else {
      break;
    }
  }
  return { inferredCadenceDays, streak };
};

/**
 * Derive a month-over-month momentum signal from the same
 * timestamp list. Compares the streak 30 days ago (using only
 * rotations with ts ≤ cutoff) against the current streak. Returns
 *   'up'    — current streak > historical  (rotating *more*
 *             consistently now than 30 days ago; classic Duolingo
 *             momentum indicator)
 *   'down'  — current streak < historical  (was tighter, has drifted)
 *   'flat'  — identical or within 1 rotation either way
 *   null    — not enough history to judge (first month, single
 *             rotation, etc.)
 *
 * Zero-cost composition: same entries list the caller already
 * passed into `computeCadenceSignature`. We never fetch anything.
 */
export const computeCadenceTrend = (entries) => {
  if (!entries || entries.length < 3) return null;
  const cutoff = Date.now() - 30 * 86400_000;
  const past = entries.filter((e) => e.ts <= cutoff);
  if (past.length < 2) return null;

  const now = computeCadenceSignature(entries);
  const then = computeCadenceSignature(past);
  if (!now?.streak || !then?.streak) return null;

  const delta = now.streak - then.streak;
  if (Math.abs(delta) <= 1) return 'flat';
  return delta > 0 ? 'up' : 'down';
};

// Cache: userId → { health, days, fetchedAt, inflight? }
const _cache = new Map();

const _isDuress = () =>
  typeof window !== 'undefined' &&
  sessionStorage.getItem('gs_duress_mode') === '1';

const _fetchDetail = async (userId) => {
  try {
    const r = await axios.get(`${API}/users/${userId}/key-history`);
    const rows = (r.data?.entries || [])
      .map((e) => ({ ts: Date.parse(e.created_at) }))
      .filter((e) => !Number.isNaN(e.ts))
      .sort((a, b) => a.ts - b.ts);
    if (rows.length === 0) return null;
    // Compute worst gap (incl. gap to now) alongside classification
    // so callers like the Nudge prompt can display "…in N days".
    let worstMs = Date.now() - rows[rows.length - 1].ts;
    for (let i = 1; i < rows.length; i++) {
      const gap = rows[i].ts - rows[i - 1].ts;
      if (gap > worstMs) worstMs = gap;
    }
    return {
      health: classifyRotationHealth(rows),
      days: Math.round(worstMs / 86400_000),
      latestAt: rows[rows.length - 1].ts,
      // Optional badge anchors — computed from the same rows, so
      // pill rendering costs nothing extra on top of the hook.
      cadence: computeCadenceSignature(rows),
      trend: computeCadenceTrend(rows),
    };
  } catch {
    return null;
  }
};

/**
 * One-shot fetch: returns the timestamp (ms) of the peer's most
 * recent rotation, or null. Used by the nudge feedback loop so we
 * can tell whether a peer actually rotated AFTER we pinged them.
 * Never cached here — the whole point is to observe fresh changes.
 */
export const fetchLatestRotationAt = async (userId) => {
  if (!userId || _isDuress()) return null;
  const d = await _fetchDetail(userId);
  return d?.latestAt || null;
};

/**
 * React hook. Returns the cached health if fresh; otherwise kicks
 * off one fetch (dedup'd across concurrent callers) and returns
 * `null` until it resolves.
 */
export const useRotationHealth = (userId) => {
  const detail = useRotationHealthDetail(userId);
  return detail?.health || null;
};

/**
 * Same lookup as `useRotationHealth` but returns the richer
 * `{ health, days }` shape for callers who want to render the
 * numeric days-since-last-rotation alongside the classification
 * (e.g. the Nudge prompt: "…hasn't rotated in 18 days").
 */
export const useRotationHealthDetail = (userId) => {
  const [detail, setDetail] = useState(() => {
    if (!userId || _isDuress()) return null;
    const cached = _cache.get(userId);
    if (cached && cached.fetchedAt && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      return cached.health
        ? { health: cached.health, days: cached.days, latestAt: cached.latestAt, cadence: cached.cadence, trend: cached.trend }
        : null;
    }
    return null;
  });

  useEffect(() => {
    if (!userId) { setDetail(null); return; }
    if (_isDuress()) { setDetail(null); return; }
    let cancelled = false;

    const cached = _cache.get(userId);
    if (cached && cached.fetchedAt && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
      setDetail(cached.health
        ? { health: cached.health, days: cached.days, latestAt: cached.latestAt, cadence: cached.cadence, trend: cached.trend }
        : null);
      return;
    }

    if (cached?.inflight) {
      cached.inflight.then((d) => {
        if (!cancelled) setDetail(d);
      });
      return;
    }

    const promise = _fetchDetail(userId).then((d) => {
      _cache.set(userId, {
        health: d?.health || null,
        days: d?.days || null,
        latestAt: d?.latestAt || null,
        cadence: d?.cadence || null,
        trend: d?.trend || null,
        fetchedAt: Date.now(),
      });
      return d;
    });
    _cache.set(userId, {
      health: cached?.health || null,
      days: cached?.days || null,
      latestAt: cached?.latestAt || null,
      cadence: cached?.cadence || null,
      trend: cached?.trend || null,
      fetchedAt: cached?.fetchedAt || 0,
      inflight: promise,
    });
    promise.then((d) => { if (!cancelled) setDetail(d); });
    return () => { cancelled = true; };
  }, [userId]);

  return detail;
};

/**
 * Map the classifier output to tailwind ring colors. Keep in one
 * place so the 3 call sites (sparkline badge, conversation avatar,
 * contact avatar) render an identical palette.
 */
export const HEALTH_RING_CLASS = {
  healthy: 'ring-2 ring-emerald-400/70',
  slipping: 'ring-2 ring-amber-400/70',
  inactive: 'ring-2 ring-rose-400/70',
};

export const HEALTH_DOT_CLASS = {
  healthy: 'bg-emerald-400',
  slipping: 'bg-amber-400',
  inactive: 'bg-rose-400',
};
